import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import { EditableText, EditableImage, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import {
  Sparkles,
  Users,
  Heart,
  ArrowRight,
  Calendar,
  Star,
  CheckCircle2,
  Eye,
  MessageCircle,
  Bell,
  Clock,
  Image,
  Palette,
  Camera,
  UserCheck,
  RefreshCw,
  Send,
  Check,
  X,
  AlertCircle,
  Smile,
  Info,
  Scissors,
  Paintbrush,
  ShoppingBag,
  Wine
} from "lucide-react";

const MUASalesPage = () => {
  const [demoTab, setDemoTab] = useState("client");
  const [flowStep, setFlowStep] = useState(1);
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const { editMode } = useAdmin();

  useEffect(() => {
    document.title = "phygital - For Makeup Artists";
  }, []);

  // Generic MUA services (based on typical Acuity setup)
  const muaServices = [
    { name: "In Studio", duration: "1 hour", price: "$150+", desc: "Camera-ready glam" },
    { name: "On Location", duration: "1 hour", price: "$250+", desc: "Travel within 30 miles" },
    { name: "Bridal", duration: "1.5 hours", price: "$300+", desc: "Your special day" },
    { name: "Special Event", duration: "1 hour", price: "$175+", desc: "Galas, photoshoots, etc." },
  ];

  // Mock client preferences
  const mockClientPrefs = {
    name: "Client",
    defaults: {
      skinType: "Combination",
      foundation: "Medium coverage, dewy finish",
      allergies: "None",
      lashes: "Natural mink preferred"
    },
    thisAppt: {
      event: "Photoshoot - Editorial",
      look: "Soft glam with bold lip",
      inspo: ["Reference 1", "Reference 2"],
      notes: "Want to try a berry lip color"
    },
    vibe: "Chat away",
    timing: "Flexible"
  };

  // Booking flow steps
  const flowSteps = [
    { num: 1, title: "Book on Acuity", desc: "Client selects service & time" },
    { num: 2, title: "Phygital Prompt", desc: "Share preferences for this appointment" },
    { num: 3, title: "MUA Reviews", desc: "See look, inspo, and confirm" },
    { num: 4, title: "Day Of", desc: "Everything ready, no guesswork" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="hidden sm:flex gap-1 bg-pink-100 text-pink-700">
              <Eye className="w-3 h-3" />
              Demo Mode
            </Badge>
            <Button 
              size="sm" 
              className="rounded-full"
              onClick={() => setShowEarlyAccessModal(true)}
              data-testid="mua-early-access-btn"
            >
              Get Early Access
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <EditableSection sectionId="mua-hero" label="MUA Hero" className={editMode ? "pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 relative overflow-hidden" : "pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 relative overflow-hidden"}>
        <div className="absolute inset-0 bg-gradient-to-br from-pink-50 via-white to-rose-50/30" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-pink-200/20 to-rose-200/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto text-center">
          <Badge className="mb-4 sm:mb-6 bg-gradient-to-r from-pink-500 to-rose-500 text-white border-0">
            <Palette className="w-3 h-3 mr-1" />
            <EditableText sectionId="mua-hero" field="badge" defaultValue="MUA Pilot by Phygital" className="inline" />
          </Badge>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            <EditableText sectionId="mua-hero" field="title_line1" defaultValue="Know Your Client's Look" className="inline" /><br />
            <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-red-500 bg-clip-text text-transparent">
              <EditableText sectionId="mua-hero" field="title_line2" defaultValue="Before They Sit Down" className="inline" />
            </span>
          </h1>
          
          <p className="text-sm sm:text-xl text-gray-700 max-w-3xl mx-auto mb-6 sm:mb-10 px-4">
            <EditableText 
              sectionId="mua-hero" 
              field="subtitle" 
              defaultValue="Keep using Acuity for booking. Phygital adds a preference layer so clients share their look, inspiration, and vibe before every appointment."
              multiline
            />
          </p>
          
          <div className="flex justify-center mb-8 sm:mb-12">
            <Button 
              size="lg" 
              className="rounded-full bg-gray-900 hover:bg-gray-800 text-sm sm:text-base px-6 sm:px-8"
              onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
            >
              See How It Works
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Value Props */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 sm:gap-6 text-[11px] sm:text-sm max-w-[320px] sm:max-w-none mx-auto">
            {[
              { icon: Calendar, text: "Works with Acuity" },
              { icon: Image, text: "Inspo before booking" },
              { icon: Bell, text: "Change notifications" },
              { icon: CheckCircle2, text: "Look approval flow" }
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 text-gray-700 whitespace-nowrap">
                <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-pink-500 flex-shrink-0" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </EditableSection>

      {/* Style & Supplies Preview - Device Mockup */}
      <section className="py-12 px-4 bg-gradient-to-br from-pink-50 via-white to-rose-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white border-0">
              <Sparkles className="w-3 h-3 mr-1" />
              What You See
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Style, Supplies, and Pre-Prep
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              Each client selects a look from your portfolio. You see the supplies and prep needed.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Phone Mockup - Client View */}
            <div className="flex justify-center">
              <div className="relative">
                {/* Phone Frame */}
                <div className="w-[280px] bg-gray-900 rounded-[2.5rem] p-3 shadow-2xl">
                  <div className="bg-white rounded-[2rem] overflow-hidden">
                    {/* Status Bar */}
                    <div className="bg-gray-100 px-4 py-2 flex justify-between items-center text-xs text-gray-700">
                      <span>9:41</span>
                      <span>Client View</span>
                    </div>
                    {/* Content */}
                    <div className="p-4 space-y-3">
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Select your look</p>
                        <p className="font-semibold text-sm">from MUA portfolio</p>
                      </div>
                      {/* Mini Portfolio Grid - EDITABLE IMAGES */}
                      <div className="grid grid-cols-3 gap-1.5">
                        {["Soft Glam", "Natural", "Bold", "Bridal", "Editorial", "Everyday"].map((look, i) => (
                          <EditableImage
                            key={i}
                            sectionId="mua-landing-portfolio"
                            field={`mini_${look.toLowerCase().replace(' ', '-')}`}
                            defaultImage={null}
                            className={`aspect-square rounded-lg overflow-hidden ${i === 0 ? 'ring-2 ring-pink-500' : ''}`}
                            placeholderClassName="bg-gradient-to-br from-pink-100 to-rose-100"
                            placeholderIcon={<Palette className="w-4 h-4 text-pink-300" />}
                          />
                        ))}
                      </div>
                      <div className="text-center">
                        <Badge className="bg-pink-100 text-pink-700 text-[10px]">Soft Glam selected</Badge>
                      </div>
                      <div className="p-2 bg-gray-50 rounded-lg">
                        <p className="text-[10px] text-gray-600 mb-1">Add your own inspo</p>
                        <div className="flex gap-1">
                          <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center">
                            <Image className="w-3 h-3 text-gray-500" />
                          </div>
                          <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center">
                            <Camera className="w-3 h-3 text-gray-500" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-600 mt-3">Client selects from your looks</p>
              </div>
            </div>

            {/* Laptop Mockup - MUA View */}
            <div className="flex justify-center">
              <div className="relative">
                {/* Laptop Frame */}
                <div className="w-[400px] bg-gray-800 rounded-t-xl p-2">
                  <div className="bg-white rounded-lg overflow-hidden">
                    {/* Browser Bar */}
                    <div className="bg-gray-100 px-3 py-1.5 flex items-center gap-2 border-b">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 rounded-full bg-red-400" />
                        <div className="w-2 h-2 rounded-full bg-yellow-400" />
                        <div className="w-2 h-2 rounded-full bg-green-400" />
                      </div>
                      <div className="flex-1 bg-white rounded px-2 py-0.5 text-[10px] text-gray-600">phygital.systems/mua/dashboard</div>
                    </div>
                    {/* Content */}
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold text-sm">Today's Appointments</span>
                        <Badge className="bg-green-100 text-green-700 text-[10px]">3 Ready</Badge>
                      </div>
                      
                      {/* Appointment Card */}
                      <div className="border border-pink-200 rounded-lg p-2 mb-2 bg-pink-50/50">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-pink-200 flex items-center justify-center text-pink-700 text-xs font-bold">J</div>
                          <div>
                            <p className="text-xs font-medium">Jordan - 9:00 AM</p>
                            <p className="text-[10px] text-gray-600">Soft Glam</p>
                          </div>
                        </div>
                        
                        {/* Supplies Section */}
                        <div className="bg-white rounded p-2 mb-2">
                          <p className="text-[10px] font-medium text-gray-700 mb-1">Supplies Needed:</p>
                          <div className="flex flex-wrap gap-1">
                            {["Foundation", "Lashes", "Lip Liner", "Setting Spray"].map((s, i) => (
                              <span key={i} className="text-[9px] px-1.5 py-0.5 bg-pink-100 text-pink-700 rounded">{s}</span>
                            ))}
                          </div>
                        </div>
                        
                        {/* Pre-Prep */}
                        <div className="bg-amber-50 rounded p-2 border border-amber-200">
                          <p className="text-[10px] font-medium text-amber-700 mb-1">Pre-Prep Required:</p>
                          <div className="flex items-center gap-1 text-[9px] text-amber-600">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Primer for oily skin</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Laptop Base */}
                <div className="w-[440px] h-3 bg-gray-700 rounded-b-xl mx-auto" />
                <p className="text-center text-sm text-gray-600 mt-3">You see supplies and prep needed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* End-of-Appointment Photo Flow */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-purple-100 text-purple-700">
              <Camera className="w-3 h-3 mr-1" />
              After the Look
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Capture and Save
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              Take a photo at the end of the appointment. Attach it to the client's private profile.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-2 border-purple-100">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <Camera className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Take Photo</h4>
                <p className="text-sm text-gray-600">Capture the final look after the appointment</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-purple-100">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <UserCheck className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Attach to Profile</h4>
                <p className="text-sm text-gray-600">Saved to client's private gallery only they can see</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-purple-100">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <Eye className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Client Views Only</h4>
                <p className="text-sm text-gray-600">Photos are private, not shown publicly</p>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            Your portfolio stays yours. Client photos are theirs.
          </p>
        </div>
      </section>

      {/* How Clients Share Preferences - Action Card Style */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-purple-100 text-purple-700">
              <Sparkles className="w-3 h-3 mr-1" />
              Client Experience
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              How Clients Share Their Look
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              After booking on Acuity, clients get a link to share their preferences
            </p>
          </div>

          {/* Action Card Demo */}
          <div className="max-w-md mx-auto">
            <Card className="border-2 border-purple-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 text-white">
                <div className="flex items-center gap-2">
                  <PhygitalIcon className="w-6 h-6" variant="light" />
                  <span className="font-semibold">Share Your Look</span>
                </div>
                <p className="text-purple-100 text-sm mt-1">Pick from the MUA's portfolio or add your own inspo</p>
              </div>
              
              <CardContent className="p-4 space-y-4">
                {/* Look Selection - EDITABLE IMAGES */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Select a look</p>
                  <div className="grid grid-cols-3 gap-2">
                    {["Soft Glam", "Natural", "Bold"].map((look, i) => (
                      <button key={i} className={`p-2 rounded-lg border-2 text-center text-xs transition-all ${i === 0 ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <EditableImage
                          sectionId="mua-landing-looks"
                          field={`look_${look.toLowerCase().replace(' ', '-')}`}
                          defaultImage={null}
                          className="w-full aspect-square rounded-lg mb-1 overflow-hidden"
                          placeholderClassName="bg-gradient-to-br from-pink-100 to-rose-100"
                          placeholderIcon={<Palette className="w-6 h-6 text-pink-300" />}
                        />
                        {look}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Products from Look */}
                <div className="p-3 bg-pink-50 rounded-lg">
                  <p className="text-xs text-pink-700 font-medium mb-2">Supplies for this look:</p>
                  <div className="flex flex-wrap gap-1">
                    {["Foundation", "Lip Liner", "Lashes", "Setting Spray"].map((p, i) => (
                      <Badge key={i} variant="secondary" className="text-xs bg-white">{p}</Badge>
                    ))}
                  </div>
                </div>

                {/* Add Own Inspo */}
                <div className="flex items-center gap-2 p-3 border border-dashed border-gray-300 rounded-lg">
                  <Image className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-600">Add your own inspiration</span>
                  <ArrowRight className="w-4 h-4 text-gray-500 ml-auto" />
                </div>

                <Button className="w-full bg-gradient-to-r from-pink-500 to-purple-500">
                  <Send className="w-4 h-4 mr-2" />
                  Send to MUA
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* MUA Prep Tracker */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-amber-100 text-amber-700">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Prep Tracker
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Track What You Need
            </h2>
            <p className="text-gray-700">
              See supplies needed for each booking and track directly in the app
            </p>
          </div>

          <Card className="border-2 border-amber-200 max-w-lg mx-auto">
            <CardHeader className="bg-amber-50">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  Upcoming: Jordan - Jan 25
                </span>
                <Badge className="bg-green-100 text-green-700">Confirmed</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Supplies Checklist</p>
                {[
                  { item: "Foundation - Medium coverage, dewy", checked: true },
                  { item: "Berry lip color (client request)", checked: false },
                  { item: "Natural mink lashes", checked: true },
                  { item: "Setting spray", checked: true },
                  { item: "Makeup wipes", checked: false },
                ].map((supply, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      supply.checked ? 'bg-green-500 border-green-500' : 'border-gray-300'
                    }`}>
                      {supply.checked && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className={`text-sm ${supply.checked ? 'text-gray-600' : 'text-gray-700'}`}>
                      {supply.item}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-pink-50 rounded-lg">
                <p className="text-xs text-pink-700">
                  <strong>Client note:</strong> "Want to try a berry lip color"
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Integration Flow */}
      <section className="py-12 sm:py-16 px-4 bg-gray-50" id="demo">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-pink-100 text-pink-700">
              <RefreshCw className="w-3 h-3 mr-1" />
              Acuity + Phygital
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Enhances Your Existing Flow
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto text-sm sm:text-base">
              Your Acuity booking stays exactly the same. Phygital adds a preference layer after they book.
            </p>
          </div>

          {/* Flow Steps */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {flowSteps.map((step, i) => (
              <div 
                key={i}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  flowStep === step.num 
                    ? 'border-pink-500 bg-pink-50' 
                    : 'border-gray-200 bg-white hover:border-pink-200'
                }`}
                onClick={() => setFlowStep(step.num)}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mb-2 ${
                  flowStep === step.num ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  {step.num}
                </div>
                <h4 className="font-semibold text-sm text-gray-900">{step.title}</h4>
                <p className="text-xs text-gray-600 mt-1">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Flow Visualization */}
          <Card className="border-2 border-pink-200 overflow-hidden">
            <CardContent className="p-0">
              {flowStep === 1 && (
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Client Books on Acuity</h3>
                      <p className="text-xs text-gray-600">Your existing booking flow - no changes needed</p>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-4 text-white max-w-md mx-auto">
                    <div className="text-xs text-gray-500 mb-3">your-booking.acuityscheduling.com</div>
                    <p className="font-semibold mb-4">Your MUA Services</p>
                    <div className="space-y-2">
                      {muaServices.map((svc, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-white/10 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{svc.name}</p>
                            <p className="text-xs text-gray-500">{svc.duration}</p>
                          </div>
                          <Badge className="bg-pink-500">{svc.price}</Badge>
                        </div>
                      ))}
                    </div>
                    <Button className="w-full mt-4 bg-pink-500 hover:bg-pink-600">
                      Book Now
                    </Button>
                  </div>
                  <p className="text-center text-sm text-gray-600 mt-4">
                    Client books like normal. Nothing changes here.
                  </p>
                </div>
              )}

              {flowStep === 2 && (
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Phygital Preference Prompt</h3>
                      <p className="text-xs text-gray-600">After booking, client gets a link to share their look</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-4 border border-pink-200 max-w-md mx-auto">
                    <div className="flex items-center gap-2 mb-4">
                      <PhygitalIcon className="w-6 h-6" />
                      <span className="font-semibold text-sm">Share Your Look</span>
                    </div>
                    <div className="space-y-3">
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-600 mb-1">Your Saved Preferences</p>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="secondary" className="text-xs">Combination skin</Badge>
                          <Badge variant="secondary" className="text-xs">Dewy finish</Badge>
                          <Badge variant="secondary" className="text-xs">Natural lashes</Badge>
                        </div>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-600 mb-1">This Appointment</p>
                        <p className="text-sm font-medium">Photoshoot - Editorial</p>
                        <p className="text-sm text-gray-700">Soft glam with bold lip</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-600 mb-2">Inspiration</p>
                        <div className="flex gap-2">
                          <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Image className="w-5 h-5 text-gray-500" />
                          </div>
                          <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Image className="w-5 h-5 text-gray-500" />
                          </div>
                          <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                            <span className="text-xs text-gray-500">+ Add</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Button className="w-full mt-4 bg-pink-500 hover:bg-pink-600">
                      <Send className="w-4 h-4 mr-2" />
                      Send to MUA
                    </Button>
                  </div>
                </div>
              )}

              {flowStep === 3 && (
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">MUA Reviews & Responds</h3>
                      <p className="text-xs text-gray-600">Approve, suggest changes, or ask questions</p>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-4 text-white max-w-md mx-auto">
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
                      <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center font-bold">J</div>
                      <div>
                        <p className="font-semibold">Client's Booking</p>
                        <p className="text-xs text-gray-500">In Studio - Jan 25 @ 2pm</p>
                      </div>
                    </div>
                    <div className="space-y-3 mb-4">
                      <div className="p-3 bg-white/10 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Look Request</p>
                        <p className="text-sm">Soft glam with bold lip • Editorial photoshoot</p>
                      </div>
                      <div className="p-3 bg-white/10 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Inspo Attached</p>
                        <div className="flex gap-2 mt-1">
                          <div className="w-12 h-12 bg-white/20 rounded flex items-center justify-center">
                            <Image className="w-4 h-4 text-white/50" />
                          </div>
                          <div className="w-12 h-12 bg-white/20 rounded flex items-center justify-center">
                            <Image className="w-4 h-4 text-white/50" />
                          </div>
                        </div>
                      </div>
                      <div className="p-3 bg-white/10 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Client Note</p>
                        <p className="text-sm italic">"Want to try a berry lip color"</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button className="flex-1 bg-green-500 hover:bg-green-600">
                        <Check className="w-4 h-4 mr-1" />
                        Approve Look
                      </Button>
                      <Button variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">
                        <MessageCircle className="w-4 h-4 mr-1" />
                        Message
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {flowStep === 4 && (
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                      <Star className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Day Of - No Guesswork</h3>
                      <p className="text-xs text-gray-600">Everything confirmed and ready</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                    <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-green-800">Look Confirmed</span>
                      </div>
                      <ul className="space-y-2 text-sm text-green-700">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Soft glam with bold berry lip
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Dewy finish, medium coverage
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Natural lashes
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          2 inspo images reviewed
                        </li>
                      </ul>
                    </div>
                    <div className="p-4 bg-pink-50 rounded-xl border border-pink-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Heart className="w-5 h-5 text-pink-600" />
                        <span className="font-semibold text-pink-800">Client Vibe</span>
                      </div>
                      <ul className="space-y-2 text-sm text-pink-700">
                        <li className="flex items-center gap-2">
                          <Smile className="w-4 h-4" />
                          Chatty - loves conversation
                        </li>
                        <li className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Flexible on timing
                        </li>
                        <li className="flex items-center gap-2">
                          <Camera className="w-4 h-4" />
                          Editorial photoshoot today
                        </li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-600 mt-6">
                    You know exactly what they want before they sit down.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Change Notification Demo */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-amber-100 text-amber-700">
              <Bell className="w-3 h-3 mr-1" />
              Real-time Updates
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Client Changes? You'll Know
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              If a client updates their look or adds new inspo, you get notified instantly.
            </p>
          </div>

          <Card className="border-2 border-amber-200 max-w-lg mx-auto">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-amber-800">Client updated their look</span>
                    <Badge className="bg-amber-200 text-amber-800 text-xs">2 min ago</Badge>
                  </div>
                  <p className="text-sm text-amber-700 mb-3">
                    Changed from "bold lip" to "natural nude lip" • Added 1 new inspo
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-500 hover:bg-green-600">
                      <Check className="w-3 h-3 mr-1" />
                      Approve Changes
                    </Button>
                    <Button size="sm" variant="outline" className="border-amber-300">
                      <MessageCircle className="w-3 h-3 mr-1" />
                      Ask Question
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-100 rounded-xl">
                  <p className="text-xs text-gray-600 mb-2 flex items-center gap-1">
                    <X className="w-3 h-3 text-red-400" />
                    Previous
                  </p>
                  <p className="text-sm line-through text-gray-500">Soft glam with bold lip</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <p className="text-xs text-green-600 mb-2 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Updated
                  </p>
                  <p className="text-sm font-medium text-green-800">Soft glam with natural nude lip</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Client vs MUA View */}
      <section className="py-12 sm:py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-pink-100 text-pink-700">
              <Heart className="w-3 h-3 mr-1" />
              Preference Sharing
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Defaults + Appointment-Specific
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              Clients save their baseline preferences. For each booking, they add their specific look and inspo.
            </p>
          </div>

          <Tabs value={demoTab} onValueChange={setDemoTab}>
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="client">Client View</TabsTrigger>
              <TabsTrigger value="mua">MUA View</TabsTrigger>
            </TabsList>

            <TabsContent value="client">
              <Card className="max-w-lg mx-auto border-2 border-pink-200">
                <CardHeader className="bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-t-lg">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Share Your Look
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-xs text-gray-600 mb-2">Your Saved Preferences</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">Combination skin</Badge>
                      <Badge variant="secondary">Medium coverage</Badge>
                      <Badge variant="secondary">Dewy finish</Badge>
                      <Badge variant="secondary">Natural lashes</Badge>
                    </div>
                  </div>

                  <div className="p-4 border rounded-xl">
                    <p className="text-sm font-medium mb-2">This Appointment</p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-600">Event Type</label>
                        <p className="text-sm">Photoshoot - Editorial</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Look Description</label>
                        <p className="text-sm">Soft glam with bold lip</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Notes</label>
                        <p className="text-sm italic text-gray-700">"Want to try a berry lip color"</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-xl">
                    <p className="text-sm font-medium mb-2">Inspiration</p>
                    <div className="flex gap-2">
                      <div className="w-20 h-20 bg-gradient-to-br from-pink-200 to-rose-200 rounded-lg flex items-center justify-center">
                        <Image className="w-6 h-6 text-pink-400" />
                      </div>
                      <div className="w-20 h-20 bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg flex items-center justify-center">
                        <Image className="w-6 h-6 text-purple-400" />
                      </div>
                      <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed">
                        <span className="text-xs text-gray-500">+ Add</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-xl">
                    <p className="text-sm font-medium mb-2 text-purple-800">Your Vibe Today</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-purple-200 text-purple-700">
                        <Smile className="w-3 h-3 mr-1" />
                        Chat away
                      </Badge>
                      <Badge variant="secondary">Flexible timing</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="mua">
              <Card className="max-w-lg mx-auto border-2 border-gray-200">
                <CardHeader className="bg-gray-900 text-white rounded-t-lg">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserCheck className="w-5 h-5" />
                    Client Preferences
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white text-lg font-bold">
                      J
                    </div>
                    <div>
                      <p className="font-semibold">Jordan</p>
                      <p className="text-xs text-gray-600">In Studio • Jan 25 @ 2pm</p>
                    </div>
                    <Badge className="ml-auto bg-green-100 text-green-700">Confirmed</Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-pink-50 rounded-lg">
                      <p className="text-xs text-pink-600 mb-1">Skin</p>
                      <p className="text-sm font-medium">Combination</p>
                    </div>
                    <div className="p-3 bg-pink-50 rounded-lg">
                      <p className="text-xs text-pink-600 mb-1">Coverage</p>
                      <p className="text-sm font-medium">Medium, Dewy</p>
                    </div>
                    <div className="p-3 bg-pink-50 rounded-lg">
                      <p className="text-xs text-pink-600 mb-1">Lashes</p>
                      <p className="text-sm font-medium">Natural</p>
                    </div>
                    <div className="p-3 bg-pink-50 rounded-lg">
                      <p className="text-xs text-pink-600 mb-1">Allergies</p>
                      <p className="text-sm font-medium">None</p>
                    </div>
                  </div>

                  <div className="p-3 border-2 border-purple-200 rounded-xl bg-purple-50">
                    <p className="text-xs text-purple-600 mb-1">This Look</p>
                    <p className="font-medium">Soft glam with bold lip</p>
                    <p className="text-sm text-gray-700 mt-1">Editorial photoshoot</p>
                    <p className="text-sm italic text-purple-700 mt-2">"Want to try a berry lip color"</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 mb-2">Inspo Images</p>
                    <div className="flex gap-2">
                      <div className="w-20 h-20 bg-gradient-to-br from-pink-200 to-rose-200 rounded-lg flex items-center justify-center">
                        <Image className="w-6 h-6 text-pink-400" />
                      </div>
                      <div className="w-20 h-20 bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg flex items-center justify-center">
                        <Image className="w-6 h-6 text-purple-400" />
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-600 mb-1">Vibe Check</p>
                    <div className="flex items-center gap-2">
                      <Smile className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">Chatty, flexible timing</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button className="flex-1 bg-green-500 hover:bg-green-600">
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button variant="outline" className="flex-1">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      Message
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Built for Every MUA
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-white">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
                  <Palette className="w-6 h-6 text-pink-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Solo MUA</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Know exactly what look your client wants before they arrive.
                </p>
                <ul className="space-y-2">
                  {[
                    "See inspo before booking",
                    "Approve or suggest changes",
                    "Build preference history"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-pink-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">MUA + Add-ons</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Offer hair, lashes, nails. One preference profile covers it all.
                </p>
                <ul className="space-y-2">
                  {[
                    "Hair styling add-on",
                    "Lash application",
                    "Nail services"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-purple-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                  <Star className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Full Glam Team</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Bridal parties, productions. Everyone sees the same preferences.
                </p>
                <ul className="space-y-2">
                  {[
                    "Share with team",
                    "Multiple artists",
                    "Unified experience"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-amber-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-pink-500 via-rose-500 to-red-500">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-4xl font-bold mb-4">
            Ready to Know Your Clients Better?
          </h2>
          <p className="text-pink-100 mb-8 text-sm sm:text-base">
            Join the pilot. Keep your Acuity booking, add phygital preferences.
          </p>
          <Button 
            size="lg" 
            className="rounded-full bg-white text-pink-600 hover:bg-pink-50"
            onClick={() => setShowEarlyAccessModal(true)}
            data-testid="mua-cta-early-access-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Built for Every Service Industry - Hidden for now */}
      {/* <AllCategoriesSection currentCategory="makeup" /> */}

      {/* Footer */}
      <SharedFooter variant="dark" />
      
      {/* Early Access Modal */}
      <EarlyAccessModal open={showEarlyAccessModal} onOpenChange={setShowEarlyAccessModal} category="makeup" />
    </div>
  );
};

export default MUASalesPage;
